import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useSearchOrbitLTerminalsQuery } from "Api/Queries/Merchants/useSearchOrbitLTerminalsQuery";
import type { OrbitLTerminalSearchResultDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES =
  Resources.Pages.Content.Merchants.OrbitLOnboarding.FindByTerminal;

type Props = {
  onTerminalSelect: (merchantPublicID: string) => void;
  onClose: () => void;
};

const getOptionKey = (option: OrbitLTerminalSearchResultDto) =>
  `${option.merchantPublicID}-${option.tid ?? ""}-${option.serialNumber ?? ""}`;

export const FindByTerminalDialog = ({ onTerminalSelect, onClose }: Props) => {
  const { t } = useResource();
  const [searchInput, setSearchInput] = useState("");

  const { data: terminals, isLoading: isSearching } =
    useSearchOrbitLTerminalsQuery({
      search: searchInput,
      enabled: searchInput.length >= 2,
    });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t(RESOURCES.Title)}</DialogTitle>
      <DialogContent>
        <Autocomplete
          options={terminals ?? []}
          loading={isSearching}
          inputValue={searchInput}
          onInputChange={(_e, value) => setSearchInput(value)}
          onChange={(_e, value) => {
            if (value?.merchantPublicID) {
              onTerminalSelect(value.merchantPublicID);
            }
          }}
          getOptionLabel={(option) => option.tid ?? option.serialNumber ?? ""}
          // Matching is done server-side (TID + SN); the default label-based
          // client filter would drop SN matches when the label shows the TID.
          filterOptions={(options) => options}
          noOptionsText={t(RESOURCES.NoResults)}
          renderOption={({ key: _key, ...optionProps }, option) => (
            <li {...optionProps} key={getOptionKey(option)}>
              <Stack gap={0.5}>
                <Stack direction="row" gap={1}>
                  {option.tid && (
                    <Chip label={`TID ${option.tid}`} size="small" />
                  )}
                  {option.serialNumber && (
                    <Chip
                      label={`SN ${option.serialNumber}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {[option.companyName, option.companyNumber]
                    .filter(Boolean)
                    .join(" · ")}
                </Typography>
              </Stack>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              label={t(RESOURCES.SearchLabel)}
              size="small"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isSearching && <CircularProgress size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t(RESOURCES.Close)}</Button>
      </DialogActions>
    </Dialog>
  );
};
